import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  CaretLeft,
  ShieldCheck,
  ShieldWarning,
  Storefront,
  Truck,
  Package,
  Globe,
  SealWarning,
  Flask,
  PaperPlaneTilt,
} from "@phosphor-icons/react";
import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { api } from "../lib/api";
import { severityColor, timeAgo, channelLabel } from "../lib/format";
import SentimentChart from "../components/SentimentChart";
import RegulatoryPanel from "../components/RegulatoryPanel";
import MessageComposer from "../components/MessageComposer";

const CHANNEL_ICON = {
  online: Globe,
  retail: Storefront,
  distributor: Truck,
  importer: Package,
};

const TOOLTIP_STYLE = {
  backgroundColor: "#FFFFFF",
  borderRadius: "0px",
  border: "1px solid #E5E2DC",
  boxShadow: "none",
  color: "#1C1B1A",
  padding: "10px 12px",
  fontFamily: "Manrope, sans-serif",
  fontSize: "12px",
};

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [sellers, setSellers] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [stats, setStats] = useState(null);
  const [activeChannel, setActiveChannel] = useState("all");
  const [activeSeverity, setActiveSeverity] = useState("all");
  const [composer, setComposer] = useState({ open: false, complaint: null });

  useEffect(() => {
    (async () => {
      try {
        const [p, s, c, st] = await Promise.all([
          api.getProduct(id),
          api.getSellers(id),
          api.getComplaints(id, 80),
          api.getProductStats(id),
        ]);
        setProduct(p);
        setSellers(s);
        setComplaints(c);
        setStats(st);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [id]);

  const filteredSellers = useMemo(
    () => (activeChannel === "all" ? sellers : sellers.filter((s) => s.channel_type === activeChannel)),
    [sellers, activeChannel]
  );

  const filteredComplaints = useMemo(
    () =>
      activeSeverity === "all"
        ? complaints
        : complaints.filter((c) => c.severity === activeSeverity),
    [complaints, activeSeverity]
  );

  const channelCounts = useMemo(() => {
    const d = { online: 0, retail: 0, distributor: 0, importer: 0 };
    sellers.forEach((s) => {
      if (d[s.channel_type] !== undefined) d[s.channel_type] += 1;
    });
    return d;
  }, [sellers]);

  const regionBars = useMemo(() => {
    if (!stats) return [];
    return (stats.by_region || []).sort((a, b) => b.value - a.value);
  }, [stats]);

  if (!product) {
    return (
      <div className="max-w-[1600px] mx-auto px-4 lg:px-10 py-16 text-center text-[#6E6B65]">
        Loading…
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-12" data-testid="product-detail">
      <Link
        to="/search"
        data-testid="back-to-search"
        className="inline-flex items-center gap-1 eyebrow text-[#6E6B65] hover:text-[#1C1B1A] mb-6"
      >
        <CaretLeft size={12} /> back
      </Link>

      {/* Hero */}
      <section className="grid grid-cols-1 lg:grid-cols-12 grid-borders border border-[#E5E2DC]">
        <div className="lg:col-span-5 bg-[#EFECE6] aspect-[4/3] lg:aspect-auto overflow-hidden">
          {product.image_url && (
            <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
          )}
        </div>
        <div className="lg:col-span-7 p-6 md:p-10 flex flex-col">
          <div className="eyebrow">{product.category}{product.launch_year ? ` · ${product.launch_year}` : ""}</div>
          <div className="font-heading text-xl mt-3 text-[#6E6B65]">{product.brand}</div>
          <h1 className="font-accent italic text-5xl sm:text-6xl leading-[1.02] mt-1 text-[#1C1B1A]">
            {product.name}
          </h1>
          <p className="mt-5 text-[15px] leading-relaxed text-[#1C1B1A]/80 max-w-xl">
            {product.description}
          </p>

          <div className="mt-5">
            <button
              type="button"
              data-testid="contact-brand-btn"
              onClick={() => setComposer({ open: true, complaint: null })}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1C1B1A] text-white text-[11px] uppercase tracking-[0.18em] font-semibold hover:bg-[#A66E4E] transition-colors"
            >
              <PaperPlaneTilt size={13} weight="bold" />
              Contact brand
            </button>
          </div>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#E5E2DC] border border-[#E5E2DC]">
            <div className="bg-white p-4">
              <div className="eyebrow">Signals</div>
              <div className="font-heading text-2xl mt-1 tabular-nums">{stats?.total ?? 0}</div>
            </div>
            <div className="bg-white p-4">
              <div className="eyebrow">Sentiment</div>
              <div
                className="font-heading text-2xl mt-1 tabular-nums"
                style={{
                  color:
                    (stats?.avg_sentiment ?? 0) >= 0.2
                      ? "#40593B"
                      : (stats?.avg_sentiment ?? 0) <= -0.2
                      ? "#D93636"
                      : "#1C1B1A",
                }}
              >
                {(stats?.avg_sentiment ?? 0).toFixed(2)}
              </div>
            </div>
            <div className="bg-white p-4">
              <div className="eyebrow">Sellers</div>
              <div className="font-heading text-2xl mt-1 tabular-nums">{sellers.length}</div>
            </div>
            <div className="bg-white p-4">
              <div className="eyebrow">Regions</div>
              <div className="font-heading text-2xl mt-1 tabular-nums">
                {new Set(sellers.map((s) => s.region)).size}
              </div>
            </div>
          </div>

          <div className="mt-8">
            <div className="eyebrow mb-3 flex items-center gap-1.5">
              <Flask size={12} /> Key ingredients
            </div>
            <div className="flex flex-wrap gap-2">
              {(product.ingredients || []).slice(0, 10).map((ing) => (
                <span
                  key={ing}
                  className="text-[11px] uppercase tracking-wider px-2.5 py-1 bg-[#EFECE6] text-[#1C1B1A] border border-[#E5E2DC]"
                >
                  {ing}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Seller channels */}
      <RegulatoryPanel statusByRegion={product.regulatory_status || {}} />

      <section className="mt-10 border border-[#E5E2DC]">
        <div className="p-6 md:p-8 border-b border-[#E5E2DC]">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="eyebrow">Where it's sold</div>
              <h2 className="font-heading text-2xl sm:text-3xl font-medium mt-1">
                Sellers across channels & regions
              </h2>
            </div>
            <div className="flex bg-white border border-[#E5E2DC]" data-testid="channel-filter">
              {["all", "online", "retail", "distributor", "importer"].map((c) => (
                <button
                  key={c}
                  type="button"
                  data-testid={`channel-${c}`}
                  onClick={() => setActiveChannel(c)}
                  className={`px-3 md:px-4 py-2 text-[11px] tracking-[0.18em] uppercase font-semibold transition-colors ${
                    activeChannel === c
                      ? "bg-[#1C1B1A] text-white"
                      : "text-[#1C1B1A] hover:bg-[#EFECE6]"
                  }`}
                >
                  {c === "all" ? `All · ${sellers.length}` : `${channelLabel[c]} · ${channelCounts[c]}`}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b-2 border-[#1C1B1A]">
                {["Seller", "Channel", "Region", "Country", "Auth", "Price", "Availability"].map((h) => (
                  <th key={h} className="px-4 md:px-6 py-3 text-[10px] tracking-[0.18em] uppercase font-semibold text-[#6E6B65]">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredSellers.map((s) => {
                const Icon = CHANNEL_ICON[s.channel_type] || Globe;
                return (
                  <tr
                    key={s.id}
                    data-testid="seller-row"
                    className="border-b border-[#E5E2DC] hover:bg-[#EFECE6]/30"
                  >
                    <td className="px-4 md:px-6 py-3">
                      <div className="font-heading font-medium text-[#1C1B1A]">{s.seller_name}</div>
                    </td>
                    <td className="px-4 md:px-6 py-3">
                      <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#6E6B65]">
                        <Icon size={12} /> {channelLabel[s.channel_type]}
                      </span>
                    </td>
                    <td className="px-4 md:px-6 py-3 text-[#1C1B1A]">{s.region}</td>
                    <td className="px-4 md:px-6 py-3 text-[#6E6B65]">{s.country}</td>
                    <td className="px-4 md:px-6 py-3">
                      {s.is_authorized ? (
                        <span className="inline-flex items-center gap-1 text-[#40593B] text-[11px] uppercase tracking-wider font-semibold">
                          <ShieldCheck size={12} weight="bold" /> authorized
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[#D93636] text-[11px] uppercase tracking-wider font-semibold">
                          <ShieldWarning size={12} weight="bold" /> unverified
                        </span>
                      )}
                    </td>
                    <td className="px-4 md:px-6 py-3 font-mono text-[#1C1B1A]">
                      {s.price_usd ? `$${s.price_usd.toFixed(2)}` : "—"}
                    </td>
                    <td className="px-4 md:px-6 py-3 text-[11px] uppercase tracking-wider text-[#6E6B65]">
                      {s.availability.replace("_", " ")}
                    </td>
                  </tr>
                );
              })}
              {filteredSellers.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-10 text-center text-[#6E6B65]">
                    No sellers in this channel.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Sentiment & regions */}
      <section className="mt-10 grid grid-cols-1 lg:grid-cols-12 grid-borders border border-[#E5E2DC]">
        <div className="lg:col-span-7 p-6 md:p-8">
          <div className="eyebrow">Sentiment trend · 30 days</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-medium mt-1 mb-6">
            Consumer signal flow
          </h2>
          <SentimentChart data={stats?.trend || []} />
        </div>
        <div className="lg:col-span-5 p-6 md:p-8">
          <div className="eyebrow">By region</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-medium mt-1 mb-6">
            Where complaints originate
          </h2>
          <div className="w-full h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={regionBars} layout="vertical" margin={{ top: 5, right: 16, left: 8, bottom: 0 }}>
                <CartesianGrid stroke="#E5E2DC" strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: "#6E6B65", fontSize: 11, fontFamily: "Manrope" }} />
                <YAxis
                  dataKey="name"
                  type="category"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#1C1B1A", fontSize: 11, fontFamily: "Manrope" }}
                  width={80}
                />
                <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: "#EFECE6" }} />
                <Bar dataKey="value" fill="#A66E4E">
                  {regionBars.map((r, i) => (
                    <Cell key={i} fill="#A66E4E" />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Complaints feed */}
      <section className="mt-10 border border-[#E5E2DC] bg-white">
        <div className="p-6 md:p-8 border-b border-[#E5E2DC]">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="eyebrow flex items-center gap-1.5">
                <SealWarning size={12} /> Consumer complaints
              </div>
              <h2 className="font-heading text-2xl sm:text-3xl font-medium mt-1">
                Raw signals & adverse reactions
              </h2>
            </div>
            <div className="flex border border-[#E5E2DC]" data-testid="severity-filter">
              {["all", "high", "medium", "low"].map((s) => (
                <button
                  key={s}
                  type="button"
                  data-testid={`severity-${s}`}
                  onClick={() => setActiveSeverity(s)}
                  className={`px-3 md:px-4 py-2 text-[11px] tracking-[0.18em] uppercase font-semibold transition-colors ${
                    activeSeverity === s
                      ? "bg-[#1C1B1A] text-white"
                      : "text-[#1C1B1A] hover:bg-[#EFECE6]"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="divide-y divide-[#E5E2DC]">
          {filteredComplaints.map((c) => {
            const sev = severityColor(c.severity);
            return (
              <div key={c.id} data-testid="complaint-row" className="p-5 md:p-6 flex gap-4">
                <div className="shrink-0 w-14 text-center">
                  <div className="eyebrow text-[9px]">{c.source}</div>
                  <div className="text-[10px] font-mono text-[#6E6B65] mt-1">{timeAgo(c.created_at)}</div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1.5 flex-wrap">
                    <span
                      className="text-[10px] uppercase tracking-[0.18em] font-semibold px-1.5 py-0.5"
                      style={{ background: sev.bg, color: sev.fg }}
                    >
                      {c.severity}
                    </span>
                    <span className="eyebrow text-[9px]">{c.category.replace("_", " ")}</span>
                    <span className="eyebrow text-[9px]">· {c.region}</span>
                    <button
                      type="button"
                      data-testid="complaint-message-brand"
                      onClick={() => setComposer({ open: true, complaint: c })}
                      className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-semibold text-[#A66E4E] hover:text-[#8F5D41] border border-[#E5E2DC] hover:border-[#A66E4E] px-2 py-1 transition-colors"
                    >
                      <PaperPlaneTilt size={10} weight="bold" /> message brand
                    </button>
                  </div>
                  <p className="text-[14px] leading-relaxed text-[#1C1B1A]">{c.text}</p>
                </div>
              </div>
            );
          })}
          {filteredComplaints.length === 0 && (
            <div className="p-10 text-center text-[#6E6B65]">No complaints at this filter.</div>
          )}
        </div>
      </section>

      <MessageComposer
        open={composer.open}
        onClose={() => setComposer({ open: false, complaint: null })}
        brand={product.brand}
        product={product}
        complaint={composer.complaint}
      />
    </div>
  );
}
